import { useCallback } from 'react';
import {
  useMutation,
  UseMutationResult,
} from '@edreamsodigeo/frontend-api-client';
import {
  CreateShoppingCartMutation,
  CreateShoppingCartMutationVariables,
} from '../../../generated/graphql';
import { CREATE_SHOPPING_CART } from 'clientState/providers/operations/createShoppingCart';
import { ThreeColumnsItineraryType } from '@components/Results/Components/ThreeColumnsItinerary/types';

type CreateShoppingCartType = (params: {
  searchId: string;
  alternativeItinerary: ThreeColumnsItineraryType;
  resident: boolean;
}) => void;

type UseCreateShoppingCartMutationType = {
  data?: UseMutationResult<
    CreateShoppingCartMutation,
    CreateShoppingCartMutationVariables
  >['data'];
  errors?: UseMutationResult<
    CreateShoppingCartMutation,
    CreateShoppingCartMutationVariables
  >['errors'];
  loading: UseMutationResult<
    CreateShoppingCartMutation,
    CreateShoppingCartMutationVariables
  >['loading'];
  mutate: CreateShoppingCartType;
};

const processSegments = (
  segments: any[],
  segmentKeys: string[] = []
): { segmentIds: string[]; flightNumbers: string } => {
  const [firstSegment] = segments;
  const segmentIds = segmentKeys;
  const flightNumbers = firstSegment.sections
    .map(({ flightCode }: { flightCode: string }) => flightCode)
    .join(',');
  return {
    segmentIds,
    flightNumbers: `${segmentKeys[0]},${flightNumbers}`,
  };
};

export const getItineraryInformation = (itinerary): any => {
  const { highlightedType, carbonFootprint, legs } = itinerary;

  const { segmentIds, flightNumbers } = legs.reduce(
    (
      result: any,
      { segments, segmentKeys }: { segments: any[]; segmentKeys: string[] }
    ) => {
      const { segmentIds, flightNumbers } = processSegments(
        segments,
        segmentKeys
      );
      return {
        segmentIds: [...result.segmentIds, ...segmentIds],
        flightNumbers: [...result.flightNumbers, flightNumbers],
      };
    },
    {
      segmentIds: [],
      flightNumbers: [],
    }
  );

  return {
    carbonFootprint,
    fareItineraryKey: itinerary.id,
    flightNumbers,
    highlightedType,
    segmentIds,
  };
};

const getPromotedItineraryTypes = (
  highlightedType: string,
  carbonFootprint: { isEco: boolean }
): any[] => {
  const promotedItineraryTypes: any[] = [];

  if (highlightedType === 'recommended') {
    promotedItineraryTypes.push('ODI_RATING');
  }

  if (carbonFootprint && carbonFootprint.isEco) {
    promotedItineraryTypes.push('GREENER_CHOICE');
  }

  return promotedItineraryTypes;
};

const useCreateShoppingCart = (): UseCreateShoppingCartMutationType => {
  const mutationResult = useMutation<
    CreateShoppingCartMutation,
    CreateShoppingCartMutationVariables
  >(CREATE_SHOPPING_CART);

  const { mutate, loading, data, errors, ...rest } = mutationResult;

  const createShoppingCartMutate = useCallback(
    ({ searchId, alternativeItinerary, resident }) => {
      const {
        highlightedType,
        carbonFootprint,
        segmentIds,
        fareItineraryKey,
        flightNumbers,
      } = getItineraryInformation(alternativeItinerary);
      mutate({
        searchId,
        segmentKeys: segmentIds?.map?.((key) => `${key}`),
        promotedItineraryTypes: getPromotedItineraryTypes(
          highlightedType,
          carbonFootprint
        ),
        fareItineraryKey,
        resident,
        flightNumbers,
        extensions: [],
      });
    },
    [mutate]
  );

  return {
    ...rest,
    loading,
    errors,
    data,
    mutate: createShoppingCartMutate,
  };
};

export default useCreateShoppingCart;
